import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, useTheme } from '@mui/material';
import { ATTACHMENT_ICON_NAME } from '../ccf-icon/ccf-icon-list';
import CcfIcon, { ATTACHMENT_ICON_SIZE } from '../ccf-icon/ccf-icon';
import { CcfAppToastMessage, CcfTooltip, useTranslator } from '@nice-ccf/ui-controls';
import { CcfLogger } from '@nice-ccf/agent-sdk';

/**
 * Event Emitter created to emit events from the audio player component
 */
import EventEmitter from 'events';
import { getActiveContactID, updateAttachments } from '../ccf-inbox/ccf-inbox.slice';
import { selectCallConferenceStatus } from '../ccf-call-conference/ccf-call-conference.slice';
import { CcfToast, setAudioRecordingState } from '@nice-ccf/ui-components';
import { eventBus } from '../ccf-audio-player/ccf-audio-player-util';
import { toast } from 'react-toastify';
 
/**
 * Creating instance for recorder
 */
//DEV NOTE - As we have removed the direct import of Recoreder from vmsg, we are creating the instance of recorder here by giving type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let recorderInstance : any;
/**
 * @remarks - Props to check the recording enable or not
 */
export interface CcfVoiceRecorderProps {
  enableRecording: boolean;
}
/**
 *  @remarks - interface to handle audio file list
 */
export interface CcfaudioFileList {
  fileList: FileList,
  url: string,
  nFile: File,
}
/**
 * Component to display voice recorder
 * @returns returns voice recorder
 * @example
 * ```
 * <CcfVoiceRecorder enableRecording={props.enableRecording}
 * ```
 */
export function CcfVoiceRecorder({
  enableRecording,
}: CcfVoiceRecorderProps) {
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const theme = useTheme();
  const ccfLogger: CcfLogger = new CcfLogger('App.react-ui-component', 'ccf-voice-recorder');
  const [isActiveCall, setIsActiveCall] = useState(false);
  const activeContactId = useSelector(getActiveContactID) || '';
   const status = useSelector(selectCallConferenceStatus);
 
  /**
     * Method to handle upload attachment
     * @param fileList - file list
     * @example uploadAttachment(fileList);
     */
  const uploadAttachment = (fileList: any) => {
    dispatch(updateAttachments({id:activeContactId,attachments:[{
      downloadLink : fileList.url,
      fileName :fileList.fileName,
      fileType:fileList.fileType,
      id:fileList.id,
      }]}));
  };
 
  /**
     * Method to check microphone permission
     * @example checkMicrophonePermission()
     */
  const checkMicrophonePermission = () => {
    // Check permission using Permissions API
    navigator?.permissions?.query({ name: 'microphone' as PermissionName })
      .then(permissionStatus => {
        if (permissionStatus.state === 'granted') {
          startRecording();
        } else {
          toast.error(<CcfAppToastMessage
             type={translate('error')} 
             titleMessage={ translate('mediaDeviceAccessDenied')} 
             descriptionMessage={translate('mediaDeviceAccessDeniedSubMsg')}/>, {
             autoClose: false,
             containerId: 'AppToastContainer',
          });
        }
      }).catch(error => {
        ccfLogger.error('Permission API not supported or error occurred:', error || '');
      });
  }
 
  /**
 * @remarks - Method to initialize recorder
 * @example - initializeRecorder()
 */
  const initializeRecorder = async () => {
    if (!recorderInstance) {
      const { Recorder } = await import('vmsg');
      recorderInstance = new Recorder({ wasmURL: 'https://unpkg.com/vmsg@0.3.0/vmsg.wasm' });
    }
  };
 
  /**
      * Method to handle recording
      * @example - startRecording
    */
  const startRecording = async () => {
    
    /**
     * We are using vmsg library which creates recorder instance and provide methods to start and stop recording
     * it first initates Audio and worker and makes use of webassembly
     */
    try{
      await recorderInstance.initAudio();
      await recorderInstance.initWorker();
      recorderInstance.startRecording();
      setIsRecordingStarted(true);
      dispatch(setAudioRecordingState({id: activeContactId, isRecordingInprogress: true}));
    } catch (error: unknown) {
      if (error instanceof Error) {
        ccfLogger.error('Error occurred while recording:', error.toString() || '');
      } else {
        ccfLogger.error('An unknown error occurred while recording','Error while recording');
      }
    }
  };
 
  /**
 * Method to get FileName for Audio Attachment
 * @example - getFileNameForAudioAttachment
 * ```
 * getFileNameForAudioAttachment()
 * ```
 */
const getFileNameForAudioAttachment = () => {
  const date = new Date();
  const localString = date.toLocaleString().replace(/[,]/g, ' -'); // e.g., "2023-10-05T14:48:00.000Z"
  const filename = `audio-${localString}.mp3`;
  return filename;
};

  /**
      * Method to stop recording
      * @example - stopRecording
      */
  const stopRecording = async () => {
    if (isRecordingStarted){
      const blob = await recorderInstance.stopRecording();
      const audioFileName = getFileNameForAudioAttachment();
      const audioRecordingFile = new File([blob], audioFileName , { type: 'audio/mpeg' });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(audioRecordingFile);
      const fileList = dataTransfer.files;
      const tempUrl = URL.createObjectURL(audioRecordingFile);
      const Id = tempUrl.split('/').pop();
      const audioFileList: any = {
        fileList: fileList,
        url: tempUrl,
        nFile: audioRecordingFile,
        fileName : audioFileName,
        fileType : 'audio/mpeg',
        id : Id,
      }
      uploadAttachment(audioFileList);
      setIsRecordingStarted(false);
      dispatch(setAudioRecordingState({id: activeContactId, isRecordingInprogress: false}));
    }
  };
 
  useEffect(() => {
    if ( status === 'conference') {
      setIsActiveCall(true);
      //Stop recording if any call is active
      if (isRecordingStarted) {
        handleDiscardEvent();
      }
    } else {
      setIsActiveCall(false);
    }
  }, [status]);

  /**
   * To initialize the recorder instance once the component is mounted
   */
  useEffect(() => {
    initializeRecorder();
  },[]);
  /**
     * Method to handle discard event
     * @example - handleDiscardEvent()
     */
  const handleDiscardEvent = () => {
    dispatch(setAudioRecordingState({id: activeContactId, isRecordingInprogress: false}));
    isRecordingStarted && recorderInstance.close();
    setIsRecordingStarted(false);
  };
 
  /**
   * To discard the recording if the user switches the interaction tab in Elevated case
   */
  useEffect(() => {
    handleDiscardEvent();
  }, [activeContactId]);
 
  useEffect(() => {
    /**
         * emit message event
         * @example - emitMessageEvent('message');
         * @param message - message to show
         */
    const handleEvent = () => {
      stopRecording();
    };
    eventBus.on('discardRecording', handleDiscardEvent);
    eventBus.on('stopRecording', handleEvent);
    return () => {
      eventBus.off('stopRecording', handleEvent);
      eventBus.off('discardRecording', handleDiscardEvent);
    };
  });
 
  return (
    enableRecording && !isActiveCall ? <>
      {!isRecordingStarted && <CcfTooltip title={translate('record')}>
        <Button data-testid='recordingStarted' sx={{ minWidth: '0', padding: '0' }} onClick={checkMicrophonePermission} disabled={isActiveCall}>
          <CcfIcon
            attachmentIcon={ATTACHMENT_ICON_NAME.RECORDING_STARTED}
            customStyle={{ padding: '0.3125rem 0 0 0.3125rem', color: theme?.palette?.text?.primary, marginTop: '0.125rem' }}
            size={ATTACHMENT_ICON_SIZE.MEDIUM} />
        </Button></CcfTooltip>}
      {isRecordingStarted && <CcfTooltip title={translate('stopRecording')}>
        <Button data-testid='recordingStopped' sx={{ minWidth: '0', padding: '0' }} onClick={() => stopRecording()}>
          <CcfIcon
            attachmentIcon={ATTACHMENT_ICON_NAME.RECORDING_STOPPED}
            customStyle={{ padding: '0.125rem 0 0 0.3125rem', marginTop: '0.25rem' }}
            size={ATTACHMENT_ICON_SIZE.MEDIUM} />
        </Button></CcfTooltip >
      }
    </> : <CcfTooltip title={translate('recordingDisabled')}>
      <Box style={{ float: 'right' }}><CcfIcon
        attachmentIcon={ATTACHMENT_ICON_NAME.REOCORDING_DISABLED}
        customStyle={{ padding: '0.333rem 0 0 0.3125rem', float: 'right', marginTop: '0.205rem' }}
        size={ATTACHMENT_ICON_SIZE.MEDIUM} />
      </Box>
    </CcfTooltip >
  );
 
};
 
export default memo(CcfVoiceRecorder);